import {Injectable, TemplateRef} from '@angular/core';
import {WindowRef, WindowService} from '@progress/kendo-angular-dialog';
import {BehaviorSubject, Observable} from 'rxjs';
import * as focusTrap from "focus-trap";
import {map} from "rxjs/operators";

interface PopupWindowEntry {
	windowRef: WindowRef;
	focusTrap: focusTrap.FocusTrap
}

// Popup types. In case a new popup type is added, add it to this enum and then handle it according to the required logic
export enum PopupType {
	POPUP_SERVICE,
	IDLE_POPUP,
	TIME_PICKER
}

/**
 ****************************************************************
 * Popup window service.
 *
 * Created by Shai Zelman on 17/04/2020.
 ****************************************************************
 */
@Injectable({providedIn: "root"})
export class PopupWindowService {
	public anyPopupsOpen: Observable<boolean>; // Observable broadcasting when there is a popup open of any type
	public openPopups: Observable<{[popupType in PopupType]?: boolean}>; // Observable broadcasting the state of the different popup types
	public isPopupsTypeOpen: (popupType: PopupType) => Observable<boolean>; // Observable broadcasting when a popup of a certain type is open

	private popupTypesOpen: {[popupType in PopupType]?: boolean} = {}; // The state of the different popup types
	private openPopupsSubject: BehaviorSubject<{[popupType in PopupType]?: boolean}> = new BehaviorSubject({}); // A Subject for the state of the different popup types

	private windowEntries: PopupWindowEntry[] = [];

	constructor(private windowService: WindowService) {
		this.anyPopupsOpen = this.openPopupsSubject.pipe(map((openPopups) => Object.values(openPopups).some((isPopupOpen) => isPopupOpen === true)));
		this.isPopupsTypeOpen = (popupType: PopupType) => this.openPopupsSubject.pipe(map((openPopups) => openPopups[popupType] === true));
		this.openPopups = this.openPopupsSubject.asObservable();
	}

	public openPopupWindow(popupContent: string | TemplateRef<any> | Function, titleTemplate: TemplateRef<any>, titleId: string, width: number = null,
	                       height: number = null, left: number = null, top: number = null, params: { [p: string]: any; } = null): Observable<any> {
		const windowRef: WindowRef = this.windowService.open({
			title: "Window",
			content: popupContent,
			draggable: false,
			resizable: false,
			titleBarContent: titleTemplate,
			width: width,
			height: height,
			left: left,
			top: top
		});

		if (params) {
			const instance = windowRef.content.instance;
			Object.keys(params).forEach(p => instance[p] = params[p]);
		}

		const windowNativeElement = windowRef.window.location.nativeElement;

		// WCAG - Set window role and aria-labelledby
		windowNativeElement.setAttribute("role", "dialog");
		windowNativeElement.setAttribute("aria-labelledby", titleId);

		// Disable background element focus
		windowNativeElement.setAttribute("tabindex", "-1");
		const trap = focusTrap.createFocusTrap(windowNativeElement, { fallbackFocus: windowNativeElement });
		trap.activate();

		this.windowEntries.push({
			windowRef: windowRef,
			focusTrap: trap
		});

		this.markPopupTypeAsOpened(PopupType.POPUP_SERVICE);

		if (this.windowEntries.length > 1) {
			this.windowEntries[this.windowEntries.length - 2].windowRef.window.location.nativeElement.setAttribute("aria-hidden", "true");
		}

		windowRef.window.onDestroy(() => {
			this.handlePopupClosing(windowRef);
		});

		return windowRef.result;
	}

	public closeAllPopups(): void {
		this.windowEntries.forEach((entry: PopupWindowEntry) => {
			entry.focusTrap.deactivate();
			entry.windowRef.close();
		});

		this.windowEntries = [];
		this.markPopupTypeAsClosed(PopupType.POPUP_SERVICE);
	}

	public closePopup(windowRef: WindowRef, arg?: any): void {
		windowRef.close(arg);

		this.handlePopupClosing(windowRef);
	}

	public closeLatestPopup(arg?: any): void {
		if (this.windowEntries.length > 0) {
			const latestEntry: PopupWindowEntry = this.windowEntries.pop();
			latestEntry.focusTrap.deactivate();
			latestEntry.windowRef.close(arg);
		}

		if (this.windowEntries.length > 0) {
			this.windowEntries[this.windowEntries.length - 1].windowRef.window.location.nativeElement.removeAttribute("aria-hidden");
		}

		this.updateOpenPopupState();
	}

	public markPopupTypeAsOpened(popupType: PopupType) {
		this.popupTypesOpen[popupType] = true;
		this.openPopupsSubject.next(this.popupTypesOpen);

		if (popupType === PopupType.IDLE_POPUP && this.windowEntries.length > 0) { // If an idle popup is opened
			this.windowEntries[this.windowEntries.length - 1].windowRef.window.location.nativeElement.setAttribute("aria-hidden", "true");  // Mark the top-most popup of the popup service as hidden
		}
	}

	public markPopupTypeAsClosed(popupType: PopupType) {
		this.popupTypesOpen[popupType] = false;
		this.openPopupsSubject.next(this.popupTypesOpen);

		if (popupType === PopupType.IDLE_POPUP && this.windowEntries.length > 0) { // If an idle popup is closed
			this.windowEntries[this.windowEntries.length - 1].windowRef.window.location.nativeElement.removeAttribute("aria-hidden"); // Mark the top-most popup of the popup service as not hidden
		}
	}

	private handlePopupClosing(windowRef: WindowRef) {
		const index: number = this.windowEntries.findIndex((entry) => entry.windowRef === windowRef);
		if (index >= 0) {
			this.windowEntries[index].focusTrap.deactivate();
			this.windowEntries.splice(index);

			if (this.windowEntries.length > 0) {
				this.windowEntries[this.windowEntries.length - 1].windowRef.window.location.nativeElement.removeAttribute("aria-hidden"); // Show the new top-most popup
			}
		}

		this.updateOpenPopupState();
	}

	private updateOpenPopupState() {
		if (this.windowEntries.length > 0) {
			this.markPopupTypeAsOpened(PopupType.POPUP_SERVICE);
		} else {
			this.markPopupTypeAsClosed(PopupType.POPUP_SERVICE);
		}
	}
}
